import type { InjectionKey } from 'vue'

export interface AppConfig {
  api_url: string
  api_token: string
  tracking_function_name: string|null
  cookie_name: string
  order_for: string
  partner_number: number|null
  checker_key_private: string|null
  checker_key_business: string|null
  on_order_change_function_name: string|null
}

const CONFIG_KEYS = [
  'api_url',
  'api_token',
  'tracking_function_name',
  'cookie_name',
  'order_for',
  'partner_number',
  'checker_key_private',
  'checker_key_business',
  'on_order_change_function_name',
] as const satisfies readonly (keyof AppConfig)[]

const defaultConfig: AppConfig = {
  api_url: '',
  api_token: '',
  tracking_function_name: null,
  cookie_name: '',
  order_for: '',
  partner_number: null,
  checker_key_private: '',
  checker_key_business: '',
  on_order_change_function_name: null
}

type ConfigKey = (typeof CONFIG_KEYS)[number]

export const appConfigProviderKey: InjectionKey<AppConfig> = Symbol('app-config')

// Present only if config.js exists (copied from config.dist.js), which is only expected for local dev.
const devConfigModules = import.meta.glob<{ default: Partial<AppConfig> }>('/config.js', { eager: true })

function readFromDataAttributes(attributes: Partial<AppConfig>): Partial<AppConfig> {
  const config: Partial<AppConfig> = {}
  for (const key of CONFIG_KEYS) {
    const value = attributes[key]
    // Chnorzig, aber notwendig für Typescript
    if (value !== undefined) {
      (config as Record<ConfigKey, AppConfig[ConfigKey]>)[key] = value
    }
  }
  return config
}

function readFromDevConfigFile(): Partial<AppConfig> {
  const module = Object.values(devConfigModules)[0]
  return module?.default ?? {}
}

export function loadAppConfig(attributes: Partial<AppConfig> = {}): AppConfig {
  const source = import.meta.env.DEV ? readFromDevConfigFile() : readFromDataAttributes(attributes)
  return { ...defaultConfig, ...source }
}
